import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, ChevronRight, Dices, Shield, Trophy, Users } from 'lucide-react';
import predictionService, { PredictionGame } from '../../services/predictionService';
import api from '../../services/api';

const ACCENT = '#3fced1';

const buildFileUrl = (path?: string | null): string | null => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${api.defaults.baseURL}${path.startsWith('/') ? '' : '/'}${path}`;
};

const statusBadge = (status?: string | null) => {
  if (status === 'open') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-[#3fced1] px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-black">
        Jetzt mitmachen
      </span>
    );
  }
  if (status === 'running') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/90 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" />
        Läuft
      </span>
    );
  }
  return null;
};

const formatDeadline = (game: PredictionGame): string | null => {
  const iso = game.registrationEnd || game.gameStart;
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const label = d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  return game.registrationEnd ? `Anmeldung bis ${label}` : `Start: ${label}`;
};

/**
 * Startseiten-Sektion: aktive Tippspiele (offen/laufend) als horizontaler
 * Slider. Admin-only-Spiele liefert der Server nur an Admins aus — sie
 * werden hier zusätzlich mit einem "Nur Admins"-Badge markiert.
 */
const PredictionGamesSection: React.FC = () => {
  const navigate = useNavigate();
  const [games, setGames] = useState<PredictionGame[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    predictionService
      .getAllGames()
      .then((list) => {
        if (!cancelled) setGames((list || []).filter((g) => g && (g.slug || g._id)));
      })
      .catch(() => {
        if (!cancelled) setGames([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Keine aktiven Tippspiele → Sektion komplett ausblenden
  if (!loading && games.length === 0) return null;

  const openGame = (game: PredictionGame) => {
    navigate(`/tippspiele/${game.slug || game._id}`);
  };

  return (
    <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
      <div className="mb-5 flex items-center justify-between gap-3">
        <div>
          <h2 className="section-heading text-2xl font-bold tracking-tight text-white sm:text-3xl">
            Tippspiele
          </h2>
          <p className="mt-1 pl-[0.9rem] text-sm text-gray-400">
            Tippe die großen Turniere und gewinne Preise
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/tippspiele')}
          className="inline-flex shrink-0 items-center gap-1 text-sm font-semibold transition-colors hover:text-white"
          style={{ color: ACCENT }}
        >
          Alle anzeigen <ChevronRight size={15} />
        </button>
      </div>

      {loading ? (
        <div className="flex gap-4 overflow-hidden">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-44 w-[320px] shrink-0 animate-pulse rounded-2xl border border-zinc-800 bg-zinc-900 sm:w-[380px]" />
          ))}
        </div>
      ) : (
        <div className="-mx-4 flex snap-x snap-mandatory gap-4 overflow-x-auto px-4 pb-3 sm:-mx-6 sm:px-6 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {games.map((game) => {
            const slideImage = buildFileUrl(
              (game.heroSlides || []).find((s: any) => s?.active !== false)?.image
            );
            const deadline = formatDeadline(game);
            return (
              <div
                key={game._id || game.slug}
                role="button"
                tabIndex={0}
                aria-label={game.name || 'Tippspiel'}
                onClick={() => openGame(game)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openGame(game);
                  }
                }}
                className="group relative h-48 w-[320px] shrink-0 cursor-pointer snap-start overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900 transition-all duration-300 hover:-translate-y-1 hover:border-[#3fced1]/50 hover:shadow-[0_16px_40px_-12px_rgba(63,206,209,0.3)] sm:w-[380px]"
              >
                {/* Hintergrund: Hero-Slide oder Gradient */}
                <div className="absolute inset-0">
                  {slideImage ? (
                    <>
                      <img src={slideImage} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/10" />
                    </>
                  ) : (
                    <div className="relative h-full w-full bg-gradient-to-br from-zinc-800 via-zinc-900 to-black">
                      <div
                        className="pointer-events-none absolute inset-0"
                        style={{ background: 'radial-gradient(70% 90% at 25% 15%, rgba(63,206,209,0.14), transparent 65%)' }}
                      />
                      <Dices size={52} className="absolute right-5 top-5 text-zinc-700" />
                    </div>
                  )}
                </div>

                {/* Badges oben */}
                <div className="absolute left-3 top-3 flex items-center gap-1.5">
                  {statusBadge(game.status)}
                  {game.adminOnly && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-purple-500/90 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white">
                      <Shield size={11} /> Nur Admins
                    </span>
                  )}
                </div>

                {/* Inhalt unten */}
                <div className="absolute inset-x-0 bottom-0 p-4">
                  <h3 className="line-clamp-1 text-lg font-bold text-white">{game.name}</h3>
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-300">
                    {game.tournament?.name && (
                      <span className="inline-flex items-center gap-1">
                        <Trophy size={12} style={{ color: ACCENT }} />
                        <span className="line-clamp-1">{game.tournament.name}</span>
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1">
                      <Users size={12} style={{ color: ACCENT }} />
                      {game.stats?.totalParticipants ?? 0} Tipper
                    </span>
                    {deadline && (
                      <span className="inline-flex items-center gap-1">
                        <CalendarDays size={12} style={{ color: ACCENT }} />
                        {deadline}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default PredictionGamesSection;
